import { createTheme } from '@mui/material/styles';
import { refineFonts } from './refineFonts';
import { CSSProperties } from 'react';

declare module '@mui/material/styles' {
	interface TypographyVariants {
		questionText: CSSProperties;
		answerText: CSSProperties;
		finalScreenTitle: CSSProperties;
		finalScreenSubtitle: CSSProperties;
		uploadDocumentsTitle: CSSProperties;
		supportingDocumentsTitle: CSSProperties;
		supportingDocumentsSubtitle: CSSProperties;
		welcomeScreenName: CSSProperties;
		contextText: CSSProperties;
		mediumToLargeBold: CSSProperties;
		mediumToLargeRegular: CSSProperties;
	}

	interface TypographyVariantsOptions {
		questionText?: CSSProperties;
		answerText?: CSSProperties;
		finalScreenTitle?: CSSProperties;
		finalScreenSubtitle?: CSSProperties;
		uploadDocumentsTitle?: CSSProperties;
		supportingDocumentsTitle?: CSSProperties;
		supportingDocumentsSubtitle?: CSSProperties;
		welcomeScreenName: CSSProperties;
		contextText: CSSProperties;
		mediumToLargeBold: CSSProperties;
		mediumToLargeRegular: CSSProperties;
	}
}
declare module '@mui/material/Typography' {
	interface TypographyPropsVariantOverrides {
		questionText: true;
		answerText: true;
		finalScreenTitle: true;
		finalScreenSubtitle: true;
		uploadDocumentsTitle: true;
		supportingDocumentsTitle: true;
		supportingDocumentsSubtitle: true;
		welcomeScreenName: true;
		contextText: true;
		mediumToLargeBold: true;
		mediumToLargeRegular: true;
	}
}
declare module '@mui/material/styles' {
	interface BreakpointOverrides {
		xxs: true;
		xs: true;
		sm: true;
		md: true;
		lg: true;
	}
}
declare module '@mui/material/styles/createPalette' {
	interface Palette {
		typographyText: {
			textPrimary: string;
			textSecondary: string;
		};
	}
}

export const getTheme = (colors: any) => {
	const theme = createTheme({
		// @ts-ignore
		typography: {
			fontFamily: 'Inter',
		},
		breakpoints: {
			values: {
				xxs: 50,
				xs: 380,
				sm: 750,
				md: 1200,
				lg: 1700,
				xl: 1930,
			},
		},
		palette: {
			primary: {
				main: colors.primary,
				dark: colors.primaryDark,
			},
			secondary: {
				main: colors.secondary,
			},
			typographyText: {
				textPrimary: colors.textPrimary,
				textSecondary: colors.textSecondary,
			},
			playbookIconButton: {
				backgroundColor: colors.playbookIconButton.backgroundColor,
				selectedBorderColor: colors.playbookIconButton.selectedBorderColor,
				selectedBackgroundColor: colors.playbookIconButton.selectedBackgroundColor,
			},
			appBackground: colors.appBackground,
		},
		components: {
			MuiButton: {
				// Overriding variants for MuiButton
				variants: [
					{
						props: { variant: 'underlineText' },
						style: {
							textTransform: 'none',
							color: '#5977EB',
							textDecoration: 'underline',
						},
					},
				],
				styleOverrides: {
					root: {
						textTransform: 'none',
					},
					contained: {
						color: colors.secondaryButtonTextColor,
					},
				},
			},
		} as any,
	});

	theme.typography.questionText = {
		...refineFonts.largeBold,
		[theme.breakpoints.up('xs')]: {
			...refineFonts.xLargeBold,
		},
		[theme.breakpoints.up('sm')]: {
			...refineFonts.xLargeBold,
		},
		[theme.breakpoints.up('md')]: {
			...refineFonts.h3Bold,
		},
	};

	theme.typography.answerText = {
		...refineFonts.mediumBold,
		[theme.breakpoints.up('xs')]: {
			...refineFonts.mediumBold,
		},
		[theme.breakpoints.up('sm')]: {
			...refineFonts.largeBold,
		},
		[theme.breakpoints.up('md')]: {
			...refineFonts.largeBold,
		},
	};

	theme.typography.finalScreenTitle = {
		...refineFonts.h4Bold,
		[theme.breakpoints.up('sm')]: {
			...refineFonts.h1Regular,
		},
	};

	theme.typography.finalScreenSubtitle = {
		...refineFonts.largeRegular,
		[theme.breakpoints.up('xs')]: {
			...refineFonts.xLargeRegular,
		},
	};

	theme.typography.uploadDocumentsTitle = {
		...refineFonts.mediumBold,
		[theme.breakpoints.up('sm')]: {
			...refineFonts.h3Bold,
		},
	};

	theme.typography.supportingDocumentsTitle = {
		...refineFonts.mediumRegular,
		[theme.breakpoints.up('sm')]: {
			...refineFonts.largeRegular,
		},
	};

	theme.typography.supportingDocumentsSubtitle = {
		...refineFonts.mediumRegular,
		color: theme.palette.typographyText.textSecondary,
		[theme.breakpoints.up('sm')]: {
			...refineFonts.largeRegular,
		},
	};
	theme.typography.welcomeScreenName = {
		...refineFonts.h4Bold,
		[theme.breakpoints.up('xs')]: {
			...refineFonts.h4Bold,
		},
		[theme.breakpoints.up('sm')]: {
			...refineFonts.h3Bold,
		},
		[theme.breakpoints.up('md')]: {
			...refineFonts.h3Bold,
		},
	};

	theme.typography.contextText = {
		...refineFonts.largeRegular,
		[theme.breakpoints.up('sm')]: {
			...refineFonts.xLargeRegular,
		},
	};

	theme.typography.mediumToLargeBold = {
		...refineFonts.mediumBold,
		[theme.breakpoints.up('sm')]: {
			...refineFonts.largeBold,
		},
	};

	theme.typography.mediumToLargeRegular = {
		...refineFonts.mediumRegular,
		[theme.breakpoints.up('sm')]: {
			...refineFonts.largeRegular,
		},
	};
	return theme;
};
